import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    model,
    OnDestroy,
    OnInit,
    output,
    signal,
    untracked
} from '@angular/core';
import { AbstractControl, FormsModule } from '@angular/forms';
import { FieldTree, FormRoot, submit } from '@angular/forms/signals';

import { BaasI18n } from '@lvflanders/bar';

import { BaasActionButtonOptions } from '../baas-action-button/baas-action-button.types';
import { BaasConfirmService } from '../baas-confirm/baas-confirm.service';
import { BaasConfirmOptions } from '../baas-confirm/baas-confirm.types';
import { BaasLayoutService } from '../layout';
import { BaasRemark } from '../remarks/baas-remark/baas-remark';
import { BaasRemarkList } from '../remarks/baas-remark-list/baas-remark-list';
import { BaasToastMessageService } from '../toast-messages/baas-toast-message-service';
import { BaasEditService } from './baas-edit.service';
import {
    BaasEditConfirmSave,
    BaasEditSaveOptions,
    BaasEditSaveOutcome,
    BaasEditSaveReason,
    BaasEditSaveResult,
    BaasEditSaveStatus,
    BaasEditValidation
} from './baas-edit.types';
import { BaasEditFormDriver } from './drivers/baas-edit-form.driver';
import { ReactiveFormDriver } from './drivers/reactive-form.driver';
import { SignalFormDriver } from './drivers/signal-form.driver';
import { BaasEditLoader } from './loader/baas-edit-loader';

@Component({
    selector: 'baas-edit',
    templateUrl: './baas-edit.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, FormRoot, NgTemplateOutlet, BaasRemarkList, BaasEditLoader, BaasRemark]
})
/**
 * BaaS edit page component supporting asynchronous operations and signals with OnPush.
 * This integrates with the new layout. */
export class BaasEdit<TModel = unknown> implements OnInit, OnDestroy {
    readonly #layout = inject(BaasLayoutService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #toastMessageService = inject(BaasToastMessageService);
    readonly #i18n = inject(BaasI18n);
    readonly #confirm = inject(BaasConfirmService);
    readonly #service = inject(BaasEditService, { optional: true });

    #pendingSaveRequests: ((value: BaasEditSaveResult) => void)[] = [];
    readonly #componentInitialized = signal(false);
    #driverFormRef: AbstractControl | FieldTree<TModel, number | string> | undefined;
    #driverInstance: BaasEditFormDriver | undefined;
    #autofocusWarningShown = false;

    /** The signal form fieldtree or legacy reactive forms control (can be a group or a plain control). */
    readonly form = input<AbstractControl | FieldTree<TModel, number | string>>();

    /** The status of the save operation. */
    readonly saveStatus = input<BaasEditSaveStatus>(undefined);
    /** Whether still loading. */
    readonly loading = input<boolean>();
    /** Whether pending. */
    readonly pending = input<boolean>();
    /** The error validations. */
    readonly validations = input<BaasEditValidation[]>();
    /** Whether readonly. */
    readonly readonly = input<boolean>(false);
    /** Whether the undo must be confirmed. */
    readonly confirmUndo = input<boolean>(false);
    /** Undo confirmation options. */
    readonly confirmUndoOptions = input<BaasConfirmOptions>();
    /** Whether to show an undo message after doing the undo. */
    readonly showUndoMessage = input<boolean>(false);
    /** Undo message to show if enabled. */
    readonly undoMessage = input<string>(this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.UNDO'));
    /** Undo tooltip. */
    readonly undoTooltip = input<string>(this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.UNDO'));
    /** Undo content. */
    readonly undoContent = input<string>(undefined);
    /** Whether to show the undo content together with the icon. */
    readonly showUndoContent = input<boolean>(undefined);
    /** Whether the save must be confirmed. */
    readonly confirmSave = input<boolean | BaasEditConfirmSave>(false);
    readonly confirmSaveOptions = input<BaasConfirmOptions>();
    /** Defines whether the autosave navigation should be blocked (meaning you have to confirm) if saving needs to be confirmed.
     * Use in combination with [confirmSave] property.*/
    readonly confirmAutosaveBlocking = input<boolean>(true);
    /** Options specific for autosave confirmation. */
    readonly confirmAutosaveOptions = input<BaasConfirmOptions>(undefined);
    /** Whether to show a save message after doing a save. */
    readonly showSaveMessage = input<boolean>(true);
    /** Save message to show if enabled. */
    readonly saveMessage = input<string>(this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.SAVE'));
    /** Undo tooltip. */
    readonly saveTooltip = input<string>(this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.SAVE'));
    /** Save content. */
    readonly saveContent = input<string>(undefined);
    /** Whether to show the save content together with the icon. */
    readonly showSaveContent = input<boolean>(undefined);
    /** Whether valid when pristine. This means if invalid we consider it valid in case it is pristine. */
    readonly validWhenPristine = input<boolean>(false);
    /** Whether the before unload event, in case the user navigates away (href), is disabled. */
    readonly beforeUnloadDisabled = input<boolean>(false);
    /** Whether to show an extra warning when the form is invalid and a save occurs. */
    readonly showSaveMessageOnInvalid = input<boolean>(true);
    /** Whether to autofocus the first invalid field when validation fails. Only works with Signal Forms. Defaults to true (undefined). */
    readonly autofocusOnInvalid = input<boolean | undefined>(undefined);
    /** Whether valid (optional). */
    readonly valid = model<boolean>(undefined);
    /** Whether pristine (optional). */
    readonly pristine = model<boolean>(undefined);
    /** Whether disabled (optional). */
    readonly disabled = model<boolean>(undefined);
    readonly submitted = model<boolean>(false);
    /** Occurs when undo is triggered. */
    readonly undo = output();
    /** Occurs when save is triggered. */
    readonly save = output<BaasEditSaveOptions>();
    /** Occurs when submit is triggered. */
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly submit = output<BaasEditSaveOptions>();
    /** Occurs when validation is requested. */
    readonly validating = output();

    /** Internal save status used in case consumer is not definding the saveStatus. */
    readonly #internalSaveStatus = signal<BaasEditSaveStatus>('idle');

    /** Effects save status being used. */
    readonly #saveStatus = computed<BaasEditSaveStatus>(() => {
        return this.saveStatus() || this.#internalSaveStatus();
    });

    readonly #reactiveForm = computed(() => {
        if (this.form() && this.form() instanceof AbstractControl) {
            return this.form() as AbstractControl;
        }
        return undefined;
    });
    // Signal form
    readonly #form = computed(() => {
        if (!this.#reactiveForm() && this.form()) return this.form() as FieldTree<TModel, number | string>;
        return undefined;
    });

    readonly #driverPristineInput = computed(() => this.pristine() ?? true);
    readonly #driverValidInput = computed(() => this.valid() ?? true);
    readonly #driverDisabledInput = computed(() => this.disabled() ?? false);
    readonly #driverReadonlyInput = computed(() => this.readonly() ?? false);
    readonly #driverValidationsInput = computed(() => this.validations() || []);

    readonly #fallbackDriver: BaasEditFormDriver = {
        pristine: this.#driverPristineInput,
        valid: this.#driverValidInput,
        disabled: this.#driverDisabledInput,
        readonly: this.#driverReadonlyInput,
        submitting: signal(false),
        pending: signal(false),
        validations: this.#driverValidationsInput,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        markAsPristine: () => {}
    };

    /** Form driver that abstracts over Reactive Forms and Signal Forms. */
    readonly #driver = computed((): BaasEditFormDriver => {
        const reactiveForm = this.#reactiveForm();
        if (reactiveForm) {
            if (!(this.#driverInstance instanceof ReactiveFormDriver) || this.#driverFormRef !== reactiveForm) {
                this.#driverInstance?.dispose?.();
                this.#driverInstance = new ReactiveFormDriver(
                    reactiveForm,
                    this.#driverPristineInput,
                    this.#driverValidInput,
                    this.#driverDisabledInput,
                    this.#driverReadonlyInput,
                    this.#driverValidationsInput,
                    (state) => {
                        this.valid.set(state.valid);
                        this.pristine.set(state.pristine);
                        this.disabled.set(state.disabled);
                    }
                );
                this.#driverFormRef = reactiveForm;
            }
            return this.#driverInstance;
        }
        const signalForm = this.#form();
        if (signalForm) {
            if (!(this.#driverInstance instanceof SignalFormDriver) || this.#driverFormRef !== signalForm) {
                this.#driverInstance?.dispose?.();
                this.#driverInstance = new SignalFormDriver(signalForm as any);
                this.#driverFormRef = signalForm;
            }
            return this.#driverInstance;
        }
        this.#driverInstance?.dispose?.();
        this.#driverInstance = undefined;
        this.#driverFormRef = undefined;
        return this.#fallbackDriver;
    });

    readonly #undoContent = computed(() => {
        return this.#showUndoContent() ? this.undoContent() || this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.UNDO') : undefined;
    });
    readonly #saveContent = computed(() => {
        return this.#showSaveContent() ? this.saveContent() || this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.SAVE') : undefined;
    });
    readonly #showSaveContent = computed<boolean>(() => this.showSaveContent() === true || (this.showSaveContent() === undefined && !!this.saveContent()));
    readonly #showUndoContent = computed<boolean>(() => this.showUndoContent() === true || (this.showUndoContent() === undefined && !!this.undoContent()));

    readonly #pristine = computed(() => this.#driver().pristine() || (this.validWhenPristine() && !this.#driver().valid()));

    readonly #valid = computed(() => {
        return this.#driver().valid() || (this.validWhenPristine() && this.#pristine());
    });

    readonly #disabled = computed(() => this.#driver().disabled());

    readonly #readonly = computed(() => this.#driver().readonly());

    readonly #contentActionsDisabled = computed(() => this.#nothingToSave() || this.#saveStatus() === 'pending' || this.#driver().pending());
    readonly #contentActionsHidden = computed(() => this.#readonly());
    readonly #showLoader = computed(() => this.loading() || this.pending() || this.#driver().submitting() || this.#driver().pending());
    readonly #loaderMessage = computed(() => {
        if (this.#driver().submitting()) {
            return this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.LOADER.SAVING');
        }
        if (this.#driver().pending()) {
            return this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.LOADER.VALIDATING');
        }
        if (this.loading()) {
            return this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.LOADER.LOADING');
        }
        if (this.pending()) {
            return this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.LOADER.PROCESSING');
        }
        return undefined;
    });
    readonly #showRemarkList = computed(() => this.submitted() && this.#validations().length);
    readonly #nothingToSave = computed(() => this.#pristine() || this.#disabled() || this.#readonly());

    readonly #validations = computed(() => this.#driver().validations());

    readonly #shouldAutofocus = computed(() => {
        const value = this.autofocusOnInvalid();
        // undefined and true both mean "do autofocus", only false disables it
        return value !== false;
    });

    protected readonly viewModel = computed(() => {
        return {
            showLoader: this.#showLoader(),
            loaderMessage: this.#loaderMessage(),
            showRemarkList: this.#showRemarkList(),
            validations: this.#validations(),
            loading: this.loading(),
            submitted: this.submitted(),
            formRoot: this.#form(),
            saveLabel: this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.SAVE')
        };
    });

    constructor() {
        effect(() => {
            const saveStatus = this.#saveStatus();
            untracked(() => {
                switch (saveStatus) {
                    case 'fulfilled': {
                        this.#markAsPristine();
                        this.submitted.set(false);
                        if (this.showSaveMessage()) {
                            this.#toastMessageService.addMessage({
                                type: 'success',
                                text: this.saveMessage()
                            });
                        }
                        this.#completeSaveRequests('saved');
                        break;
                    }
                    case 'error': {
                        this.#completeSaveRequests('error');
                        break;
                    }
                }
            });
        });

        effect(() => {
            const disabled = this.disabled();
            untracked(() => {
                this.#driver().syncDisabled?.(disabled);
            });
        });

        effect(() => {
            if (!this.#componentInitialized()) {
                return;
            }

            const driver = this.#driver();
            untracked(() => {
                driver.onInit?.(this.#destroyRef);
            });
        });

        effect(() => {
            const autofocusValue = this.autofocusOnInvalid();
            const isReactiveForm = !!this.#reactiveForm();
            untracked(() => {
                if (autofocusValue === true && isReactiveForm && !this.#autofocusWarningShown) {
                    console.warn('[BaasEdit] autofocusOnInvalid is only supported with Signal Forms, not with Reactive Forms');
                    this.#autofocusWarningShown = true;
                }
            });
        });

        effect(() => this.#layout.contentActions.set(this.#contentActions()));
    }

    ngOnInit(): void {
        this.#componentInitialized.set(true);
        if (this.#service) {
            // register callback when save is requested
            this.#service._saveRequested = (value) => {
                this.#pendingSaveRequests.push(value.resolve);
                if (this.#saveStatus() !== 'pending') this.#submit(value.options);
            };
        }

        this.#defineBeforeUnload();
    }

    ngOnDestroy(): void {
        this.#driverInstance?.dispose?.();
        this.#driverInstance = undefined;
        this.#driverFormRef = undefined;
        this.#componentInitialized.set(false);
        this.#layout.contentActions.set([]);

        window.onbeforeunload = null;

        if (this.#service) this.#service._saveRequested = undefined;

        this.#completeSaveRequests('destroy');
    }

    readonly #contentActions = computed(() => {
        return [
            {
                icon: {
                    class: 'fas fa-save',
                    iconOnly: !this.#showSaveContent(),
                    srText: this.saveTooltip()
                },
                hidden: this.#contentActionsHidden(),
                disabled: this.#contentActionsDisabled(),
                action: () => this.#submit({ reason: 'regular' }),
                tooltip: this.saveTooltip(),
                content: this.#saveContent()
            },
            {
                icon: {
                    class: 'fas fa-undo',
                    iconOnly: !this.#showUndoContent(),
                    srText: this.undoTooltip()
                },
                hidden: this.#contentActionsHidden(),
                disabled: this.#contentActionsDisabled(),
                action: () => this.#undo(),
                tooltip: this.undoTooltip(),
                content: this.#undoContent()
            }
        ] as BaasActionButtonOptions[];
    });

    #undo(): void {
        const undo = () => {
            this.undo.emit();
            this.#markAsPristine();
            this.submitted.set(false);
            if (this.showUndoMessage()) {
                this.#toastMessageService.addMessage({
                    type: 'info',
                    text: this.undoMessage()
                });
            }
        };
        if (this.confirmUndo()) {
            const confirmOptions = this.confirmUndoOptions() ? this.confirmUndoOptions() : {};
            if (!confirmOptions.message) confirmOptions.message = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_UNDO');
            if (!confirmOptions.title) confirmOptions.title = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE');
            this.#confirm.confirm(confirmOptions).subscribe(() => undo());
        } else undo();
    }

    #skipSave() {
        this.#completeSaveRequests('nothingToSave');
    }

    protected onNativeSubmit(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (!this.#contentActionsDisabled()) {
            void this.#submit({ reason: 'regular' });
        }
    }

    async #submit(options: BaasEditSaveOptions): Promise<void> {
        const submitRoutine = async () => {
            this.submitted.set(true);
            this.validating.emit();
            this.submit.emit(options);

            if (this.#form()) {
                // Signal Forms: let submit() handle validation internally.
                // It returns false when invalid, so we check after the fact.
                this.#internalSaveStatus.set('pending');
                const submitSucceeded = await submit(this.#form());
                if (submitSucceeded) {
                    this.#internalSaveStatus.set('fulfilled');
                    this.save.emit(options);
                } else if (!this.#valid()) {
                    this.#internalSaveStatus.set(undefined);
                    this.#tryEmitSaveMessageOnInvalid(options.reason);
                    if (this.#shouldAutofocus()) {
                        this.#driver().focusFirstInvalidField?.();
                    }
                    this.#completeSaveRequests('validation');
                } else {
                    this.#internalSaveStatus.set('error');
                    this.#completeSaveRequests('error');
                }
            } else if (this.#valid()) {
                this.save.emit(options);
            } else {
                // show an extra message why validation has failed
                this.#tryEmitSaveMessageOnInvalid(options.reason);
                this.#completeSaveRequests('validation');
            }
        };
        if (this.#nothingToSave()) {
            this.#completeSaveRequests('nothingToSave');
        } else {
            if (this.confirmSave() === true || this.confirmSave() === 'always' || (options.reason === 'autosave' && this.confirmSave() === 'autosave')) {
                const confirmOptions =
                    options.reason === 'autosave' && this.confirmAutosaveOptions() ? this.confirmAutosaveOptions() : this.confirmSaveOptions() || {};

                const nonBlockingAutosave = options.reason === 'autosave' && !this.confirmAutosaveBlocking();
                if (!confirmOptions.message) {
                    if (nonBlockingAutosave) confirmOptions.message = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_AUTOSAVE_NON_BLOCKING');
                    else confirmOptions.message = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_SAVE');
                }
                if (!confirmOptions.title) {
                    if (nonBlockingAutosave) confirmOptions.title = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.CONFIRM_TITLE_NON_BLOCKING');
                    else confirmOptions.title = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE');
                }

                if (nonBlockingAutosave && !confirmOptions.refuteText)
                    confirmOptions.refuteText = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.CONFIRM_FALSE_NON_BLOCKING');
                if (nonBlockingAutosave && !confirmOptions.confirmText)
                    confirmOptions.confirmText = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.CONFIRM_TRUE_NON_BLOCKING');

                if (!nonBlockingAutosave) this.#confirm.confirm(confirmOptions).subscribe(async () => await submitRoutine());
                else {
                    this.#confirm.open(confirmOptions).subscribe(async (confirm) => {
                        // NOTE watch out can also be undefined (in this case we do nothing)
                        if (confirm === true) await submitRoutine();
                        else if (confirm === false) this.#skipSave();
                    });
                }
            } else await submitRoutine();
        }
    }

    #markAsPristine() {
        this.#driver().markAsPristine();
    }

    #defineBeforeUnload(): void {
        window.onbeforeunload = (e: BeforeUnloadEvent) => {
            // in case nothing changed or not active just return
            if (this.beforeUnloadDisabled() || this.#nothingToSave()) {
                return;
            }
            e.preventDefault();
            return this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.LEAVE_PAGE');
        };
    }

    #completeSaveRequests(outcome: BaasEditSaveOutcome): void {
        const success = outcome === 'saved' || outcome === 'nothingToSave';
        this.#pendingSaveRequests.forEach((resolve) => resolve({ success, outcome }));
        this.#pendingSaveRequests = [];
    }

    #tryEmitSaveMessageOnInvalid(reason: BaasEditSaveReason): void {
        if (this.showSaveMessageOnInvalid()) {
            this.#toastMessageService.addMessage({
                text: this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.' + (reason === 'autosave' ? 'INVALID_AUTOSAVE' : 'INVALID')),
                type: 'error'
            });
        }
    }
}
